import { Button, Modal } from 'ccs-ui-components';
import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import DATATYPE from '../DataTypesConstants';
import { DataTypeInterface, Field, TypeNode, Utils } from '../Utils';
import './DataTypesList.scss';

interface DataTypesListInterface {
  field: Field,
  dataTypeClick: (dataType: DataTypeInterface, field: Field) => void,
  arrayChildDataTypeClick: (dataType: DataTypeInterface, field: Field) => void,
  numberChildDataTypeClick?: (dataType: DataTypeInterface, field: Field) => void,
  arrayNumberChildDataTypeClick?: (dataType: DataTypeInterface, field: Field) => void
}

export const DataTypesList = ({ field: curentField, dataTypeClick, arrayChildDataTypeClick, numberChildDataTypeClick = undefined, arrayNumberChildDataTypeClick = undefined }: DataTypesListInterface) => {
  const [showArrayChild, setArrayChild] = useState(false);
  const [showNumberChild, setShowNumberChild] = useState(false);
  const [showArrayNumberChild, setShowArrayNumberChild] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [dataTypeEventState, setDataTypeEventState] = useState<React.MouseEvent<HTMLButtonElement>>();
  const [dataTypeItemState, setDataTypeItemState] = useState<DataTypeInterface>();
  const defSelArrChildDataType = { dataType: 'string', id: 4, text: 'String' };
  const defSelNumberChildDataType = { dataType: 'int', id: 5, text: 'Number' };
  const { t: translate } = useTranslation();

  /**
   * @method dataTypeClickHandlerWrapper
   * @description Handles the click event for a data type item.
   * @param event - The click event.
   * @param dataTypeItem - The data type item being clicked.
   */
  const dataTypeClickHandlerWrapper = (event: React.MouseEvent<HTMLButtonElement>, dataTypeItem: DataTypeInterface) => {
    const eventTarget = event.target as HTMLButtonElement;
    if (event.currentTarget.classList.contains('data-type-selected') && document.querySelectorAll('.main-datatypes-list > .data-type-selected').length === 1) {
      return;
    }
    setDataTypeItemState(dataTypeItem);
    setDataTypeEventState(event);

    if ((dataTypeItem.dataType === 'record' && Utils.checkTypes(curentField.type, 'array')) || (dataTypeItem.dataType === 'array' && Utils.checkTypes(curentField.type, 'record'))) {
      setShowConfirmModal(true);
    } else {
      eventTarget.classList.toggle('data-type-selected');
      dataTypeClickHandler(event, dataTypeItem);
    }
  };

  /**
   * @method arrayChildDataTypeClickHandler
   * @description Handles the click event for array child data types.
   * @param event - The click event.
   * @param dataTypeItem - The data type item.
   */
  const arrayChildDataTypeClickHandler = (event: React.MouseEvent<HTMLButtonElement>, dataTypeItem: DataTypeInterface) => {
    if (event.currentTarget.classList.contains('data-type-selected') && document.querySelectorAll('.child-datatypes-list > .data-type-selected').length === 1) {
      return;
    }
    event.currentTarget.classList.toggle('data-type-selected');

    // If Number is selected in array context, show the nested number items type section
    // Don't call numberChildDataTypeClick here as it would affect the main field type
    if (dataTypeItem.dataType === 'int') {
      if (event.currentTarget.classList.contains('data-type-selected')) {
        setShowArrayNumberChild(true);
        // Automatically select Integer when Number is first selected in array context (after field is updated)
        setTimeout(() => {
          if (arrayNumberChildDataTypeClick) {
            arrayNumberChildDataTypeClick(defSelNumberChildDataType, curentField);
          }
        }, 0);
      } else {
        setShowArrayNumberChild(false);
      }
    } else {
      // Hide number child section when other datatypes are selected
      setShowArrayNumberChild(false);
    }

    arrayChildDataTypeClick(dataTypeItem, curentField);
  };

  /**
   * @method numberChildDataTypeClickHandler
   * @description Handles the click event for number child data types (Integer/Double).
   * @param event - The click event.
   * @param dataTypeItem - The data type item.
   */
  const numberChildDataTypeClickHandler = (event: React.MouseEvent<HTMLButtonElement>, dataTypeItem: DataTypeInterface) => {
    const isSelected = event.currentTarget.classList.contains('data-type-selected');
    const otherButton = dataTypeItem.dataType === 'int'
      ? document.querySelector('#number-child-double')
      : document.querySelector('#number-child-int');
    const isOtherSelected = otherButton?.classList.contains('data-type-selected');

    // If clicking the selected one, deselect it
    if (isSelected) {
      event.currentTarget.classList.remove('data-type-selected');
      if (numberChildDataTypeClick) {
        // Pass a special flag or null to indicate deselection
        numberChildDataTypeClick({ dataType: '', id: 0, text: '' }, curentField);
      }
    } else {
      // Deselect the other one if selected, then select this one
      if (isOtherSelected) {
        otherButton?.classList.remove('data-type-selected');
      }
      event.currentTarget.classList.add('data-type-selected');
      if (numberChildDataTypeClick) {
        numberChildDataTypeClick(dataTypeItem, curentField);
      }
    }
  };

  /**
   * @method getButtonClass
   * @description Returns the class name for a data type button.
   * @param dataType - The data type to check against.
   * @returns The class name for the data type button.
   */
  const getButtonClass = (dataType: string) => {
    let classNames = 'data-type-button';
    // For Number (int), also check for 'double' since Double selection should keep Number selected
    if (dataType === 'int') {
      // Check if 'int' or 'double' exists in the main field type (not in array items)
      if (Array.isArray(curentField.type)) {
        // Check if 'int' or 'double' is directly in the array (not inside an array type's items)
        const hasIntOrDouble = curentField.type.some((type: string | TypeNode) => {
          if (type === 'int' || type === 'double') {
            return true;
          }
          // Don't count if it's inside an array type's items
          if (typeof type === 'object' && type.type === 'array') {
            return false;
          }
          if (typeof type === 'object' && (type.type === 'int' || type.type === 'double')) {
            return true;
          }
          return false;
        });
        if (hasIntOrDouble) {
          classNames += ' data-type-selected';
        }
      } else if (curentField.type === 'int' || curentField.type === 'double') {
        classNames += ' data-type-selected';
      } else if (typeof curentField.type === 'object' && (curentField.type.type === 'int' || curentField.type.type === 'double')) {
        classNames += ' data-type-selected';
      }
    } else if (Utils.checkTypes(curentField.type, dataType)) {
      classNames += ' data-type-selected';
    }
    return classNames;
  };

  /**
   * @method isDataTypeSelected
   * @description Checks if a specific data type is selected.
   * @param dataType The data type to check.
   * @returns True if the data type is selected, false otherwise.
   */
  const isDataTypeSelected = (dataType: string) => {
    if ((curentField.type as TypeNode).items) {
      // For Number (int), also check for 'double' since Double selection should keep Number selected
      if (dataType === 'int') {
        return Utils.checkTypes((curentField.type as TypeNode).items ?? [], 'int') || Utils.checkTypes((curentField.type as TypeNode).items ?? [], 'double');
      }
      return Utils.checkTypes((curentField.type as TypeNode).items ?? [], dataType);
    }
    return false;
  };

  /**
   * @method getChildButtonClass
   * @description Returns the class name for the child button based on the data type.
   * @param dataType The data type to check.
   * @returns The class name for the child button.
   */
  const getChildButtonClass = (dataType: string) => {
    let classNames = 'data-type-button';

    if (Utils.checkTypes(curentField.type, 'array')) {
      if (Array.isArray(curentField.type)) {
        (curentField.type).forEach((dataTypeItem: (TypeNode | string)) => {
          if (Utils.checkTypes(dataTypeItem, 'array')) {
            if (typeof dataTypeItem === 'object' && dataTypeItem.items) {
              // For Number (int), also check for 'double' since Double selection should keep Number selected
              if (dataType === 'int') {
                if (Utils.checkTypes(dataTypeItem.items, 'int') || Utils.checkTypes(dataTypeItem.items, 'double')) {
                  classNames += ' data-type-selected';
                }
              } else if (Utils.checkTypes(dataTypeItem.items, dataType)) {
                classNames += ' data-type-selected';
              }
            }
          }
        });
      } else if (isDataTypeSelected(dataType)) {
        classNames += ' data-type-selected';
      }

      return classNames;
    }
    return classNames;
  };

  /**
   * @method getNumberChildButtonClass
   * @description Returns the class name for the number child button based on the data type in main context.
   * @param dataType The data type to check (int or double).
   * @returns The class name for the number child button.
   */
  const getNumberChildButtonClass = (dataType: string) => {
    let classNames = 'data-type-button';

    // Check main context - allow 'int' or 'double' to be selected even if Array is also selected
    // The key is to check if 'int' or 'double' exists in the main field type, not in array items
    if (Array.isArray(curentField.type)) {
      // Check if this specific dataType is in the array (but not inside an array type's items)
      const hasIntOrDouble = curentField.type.some((type: string | TypeNode) => {
        // Only count if it's a direct type, not inside an array object's items
        if (type === dataType) {
          return true;
        }
        if (typeof type === 'object' && type.type === dataType) {
          return true;
        }
        // Don't count if it's inside an array type's items
        if (typeof type === 'object' && type.type === 'array') {
          return false;
        }
        return false;
      });
      if (hasIntOrDouble) {
        classNames += ' data-type-selected';
      }
    } else if (curentField.type === dataType || (typeof curentField.type === 'object' && curentField.type.type === dataType)) {
      // For single type, check if it matches and is not an array type
      if (typeof curentField.type !== 'object' || curentField.type.type !== 'array') {
        classNames += ' data-type-selected';
      }
    }

    return classNames;
  };

  /**
   * @method getArrayNumberChildButtonClass
   * @description Returns the class name for the number child button in array items context.
   * @param dataType The data type to check (int or double).
   * @returns The class name for the array number child button.
   */
  const getArrayNumberChildButtonClass = (dataType: string) => {
    let classNames = 'data-type-button';

    // Only check array items context
    if (Utils.checkTypes(curentField.type, 'array')) {
      let arrayType: TypeNode | string | undefined;
      if (Array.isArray(curentField.type)) {
        arrayType = curentField.type.find((type: TypeNode | string) => Utils.checkTypes(type, 'array'));
      } else if (typeof curentField.type === 'object' && curentField.type.type === 'array') {
        arrayType = curentField.type;
      }

      if (arrayType && typeof arrayType === 'object' && arrayType.items) {
        const items = Array.isArray(arrayType.items) ? arrayType.items : [arrayType.items];
        if (items.includes(dataType) || items.some((item: TypeNode | string) =>
          (typeof item === 'object' && item.type === dataType))) {
          classNames += ' data-type-selected';
        }
      }
    }

    return classNames;
  };

  /**
   * @method dataTypeClickHandler
   * @description Handles the click event for a data type item.
   * @param event - The click event.
   * @param dataTypeItem - The data type item being clicked.
   * @returns False.
   */
  const dataTypeClickHandler = (event: React.MouseEvent<HTMLButtonElement>, dataTypeItem: DataTypeInterface) => {
    const eventTarget = event.target as HTMLButtonElement;
    if (dataTypeItem.dataType === 'array') {
      if (eventTarget.classList.contains('data-type-selected')) {
        arrayChildDataTypeClick(defSelArrChildDataType, curentField);
        setArrayChild(true);
      } else {
        setArrayChild(false);
        dataTypeClick(dataTypeItem, curentField);
      }
    } else if (dataTypeItem.dataType === 'int') {
      if (eventTarget.classList.contains('data-type-selected')) {
        // Number is being selected, show the number child section
        setShowNumberChild(true);
        dataTypeClick(dataTypeItem, curentField);
        // Automatically select Integer when Number is first selected (after a small delay to ensure field is updated)
        setTimeout(() => {
          if (numberChildDataTypeClick) {
            numberChildDataTypeClick(defSelNumberChildDataType, curentField);
          }
        }, 0);
      } else {
        // Number is being deselected, hide the number child section
        setShowNumberChild(false);
        dataTypeClick(dataTypeItem, curentField);
      }
    } else {
      // Don't hide number child section when other datatypes are selected
      // Only hide when Number itself is deselected
      dataTypeClick(dataTypeItem, curentField);
    }
    return false;
  };

  /**
   * @method isDataTypeEventItemState
   * @description Checks if the data type event item state exists.
   * @returns {boolean} True if the data type event item state exists, false otherwise.
   */
  const isDataTypeEventItemState = () => dataTypeEventState && dataTypeItemState;

  /**
   * @method onConfirmSwap
   * @description Handles the confirmation of a swap operation.
   */
  const onConfirmSwap = () => {
    const eventTarget = dataTypeEventState?.target as HTMLButtonElement;

    if (dataTypeItemState?.dataType === 'record') {
      // Remove Array
      if (isDataTypeEventItemState()) {
        setArrayChild(false);
        const removeDataType = DATATYPE.find((item) => item.dataType === 'array');
        if (removeDataType?.dataType) dataTypeClick(removeDataType, curentField);
      }
    } else if (dataTypeItemState?.dataType === 'array') {
      // Remove Object
      if (isDataTypeEventItemState()) {
        const removeDataType = DATATYPE.find((item) => item.dataType === 'record');
        if (removeDataType?.dataType) dataTypeClick(removeDataType, curentField);
      }
    }
    if (dataTypeEventState && dataTypeItemState) {
      eventTarget.classList.toggle('data-type-selected');
      dataTypeClickHandler(dataTypeEventState, dataTypeItemState);
    }
    setShowConfirmModal(false);
  };

  /**
   * @method onCancelSwap
   * @description Cancels the swap operation and hides the confirm modal.
   */
  const onCancelSwap = () => {
    setShowConfirmModal(false);
  };

  useEffect(() => {
    if (Utils.checkTypes(curentField.type, 'array')) {
      setArrayChild(true);
      // Check if Number (int) is selected in array items
      if (Utils.checkTypes(curentField.type, 'array')) {
        const arrayType = Array.isArray(curentField.type)
          ? curentField.type.find((type: TypeNode | string) => Utils.checkTypes(type, 'array'))
          : curentField.type;
        if (arrayType && typeof arrayType === 'object' && arrayType.items) {
          const items = Array.isArray(arrayType.items) ? arrayType.items : [arrayType.items];
          const hasIntInArray = items.some((item: TypeNode | string) =>
            item === 'int' || item === 'double' || (typeof item === 'object' && (item.type === 'int' || item.type === 'double')));
          if (hasIntInArray) {
            setShowArrayNumberChild(true);
          } else {
            // If Number is selected in array but no int/double is set, default to int
            const hasNumberInArray = items.some((item: TypeNode | string) =>
              (typeof item === 'object' && item.type === 'int') || item === 'int');
            if (hasNumberInArray && arrayNumberChildDataTypeClick) {
              // Set Integer as default in array context
              arrayNumberChildDataTypeClick(defSelNumberChildDataType, curentField);
            }
          }
        }
      }
    } else {
      setArrayChild(false);
      setShowArrayNumberChild(false);
    }
    // Only show number child section if 'int' or 'double' is selected (Number datatype) in main context
    // This handles both when Number is first selected (int) and when Double is chosen (double)
    // Check if 'int' or 'double' exists in main field type (not in array items)
    const hasIntOrDoubleInMain = Array.isArray(curentField.type)
      ? curentField.type.some((type: string | TypeNode) => {
          if (type === 'int' || type === 'double') {
            return true;
          }
          // Don't count if it's inside an array type's items
          if (typeof type === 'object' && type.type === 'array') {
            return false;
          }
          if (typeof type === 'object' && (type.type === 'int' || type.type === 'double')) {
            return true;
          }
          return false;
        })
      : curentField.type === 'int' || curentField.type === 'double' || (typeof curentField.type === 'object' && (curentField.type.type === 'int' || curentField.type.type === 'double'));

    if (hasIntOrDoubleInMain) {
      // Only show if not exclusively in array context
      setShowNumberChild(true);
    } else {
      setShowNumberChild(false);
    }
  }, [curentField.type]);

  return (
    <div className="datatypes-list">
      <div className="main-datatypes-list">
        <div className="datatypes-header">{translate('schemaGeneratorText.datatypeListHeader')}</div>
        {DATATYPE.map((item) => (
          <Button variant="primary" className={getButtonClass(item.dataType)} key={item.id} data-testid="btn-test-data-id" onClick={(e) => dataTypeClickHandlerWrapper(e, item)}>
            {item.text}
          </Button>
        ))}
      </div>
      {
        showArrayChild && (
          <div className="child-datatypes-list">
            <div className="datatypes-header">{translate('schemaGeneratorText.arrayItemsTypeHeader')}</div>
            {DATATYPE.filter((item) => item.dataType !== 'array').map((item, index, filteredArray) => (
              <React.Fragment key={`child-${item.id}`}>
                <Button variant="primary" id={`child-${item.id}`} data-testid="btn-test-arry-child-id" className={getChildButtonClass(item.dataType)} onClick={(e) => arrayChildDataTypeClickHandler(e, item)}>
                  {item.text}
                </Button>
                {index < filteredArray.length - 1 && <span className="datatype-separator" />}
              </React.Fragment>
            ))}
            {
              showArrayNumberChild && (
                <div className="array-number-child-datatypes-list">
                  <div className="datatypes-header array-number-header">
                    <span className="array-number-indicator">→</span>
                    {translate('schemaGeneratorText.numberItemsTypeHeader')}
                  </div>
                  <Button
                    variant="primary"
                    key="array-number-child-int"
                    id="array-number-child-int"
                    data-testid="btn-test-array-number-child-int"
                    className={getArrayNumberChildButtonClass('int')}
                    onClick={(e) => {
                      const isSelected = e.currentTarget.classList.contains('data-type-selected');
                      const otherButton = document.querySelector('#array-number-child-double');
                      const isOtherSelected = otherButton?.classList.contains('data-type-selected');

                      if (isSelected) {
                        // Deselect this one
                        e.currentTarget.classList.remove('data-type-selected');
                        if (arrayNumberChildDataTypeClick) {
                          arrayNumberChildDataTypeClick({ dataType: '', id: 0, text: '' }, curentField);
                        }
                      } else {
                        // Deselect the other one if selected, then select this one
                        if (isOtherSelected) {
                          otherButton?.classList.remove('data-type-selected');
                        }
                        e.currentTarget.classList.add('data-type-selected');
                        if (arrayNumberChildDataTypeClick) {
                          arrayNumberChildDataTypeClick({ dataType: 'int', id: 5, text: 'Number' }, curentField);
                        }
                      }
                    }}
                  >
                    Integer
                  </Button>
                  <span className="datatype-separator" />
                  <Button
                    variant="primary"
                    key="array-number-child-double"
                    id="array-number-child-double"
                    data-testid="btn-test-array-number-child-double"
                    className={getArrayNumberChildButtonClass('double')}
                    onClick={(e) => {
                      const isSelected = e.currentTarget.classList.contains('data-type-selected');
                      const otherButton = document.querySelector('#array-number-child-int');
                      const isOtherSelected = otherButton?.classList.contains('data-type-selected');

                      if (isSelected) {
                        // Deselect this one
                        e.currentTarget.classList.remove('data-type-selected');
                        if (arrayNumberChildDataTypeClick) {
                          arrayNumberChildDataTypeClick({ dataType: '', id: 0, text: '' }, curentField);
                        }
                      } else {
                        // Deselect the other one if selected, then select this one
                        if (isOtherSelected) {
                          otherButton?.classList.remove('data-type-selected');
                        }
                        e.currentTarget.classList.add('data-type-selected');
                        if (arrayNumberChildDataTypeClick) {
                          arrayNumberChildDataTypeClick({ dataType: 'double', id: 7, text: 'Double' }, curentField);
                        }
                      }
                    }}
                  >
                    Double
                  </Button>
                </div>)
            }
          </div>)
      }
      {
        showNumberChild && (
          <div className="number-child-datatypes-list">
            <div className="datatypes-header">{translate('schemaGeneratorText.numberItemsTypeHeader')}</div>
            <Button variant="primary" key="number-child-int" id="number-child-int" data-testid="btn-test-number-child-int" className={getNumberChildButtonClass('int')} onClick={(e) => numberChildDataTypeClickHandler(e, { dataType: 'int', id: 5, text: 'Number' })}>
              Integer
            </Button>
            <span className="datatype-separator" />
            <Button variant="primary" key="number-child-double" id="number-child-double" data-testid="btn-test-number-child-double" className={getNumberChildButtonClass('double')} onClick={(e) => numberChildDataTypeClickHandler(e, { dataType: 'double', id: 7, text: 'Double' })}>
              Double
            </Button>
          </div>)
      }
      <Modal
        modalTitle={translate('schemaGeneratorText.modalTitle')}
        modalBody={translate('schemaGeneratorText.modalDesc')}
        modalShow={showConfirmModal}
        onCloseModal={onCancelSwap}
        onSaveModal={onConfirmSwap}
        btnCloseText={translate('schemaGeneratorText.cancleBtn') ?? 'Cancel'}
        btnSaveText={translate('schemaGeneratorText.confirmBtn') ?? 'Confirm'}
        dataTestId="remove-datatype"
        disableSaveBtn={false}
        disableCloseBtn={false}
        alignToCentre
      />
    </div>
  );
};
